import {Box, Typography} from "@mui/material";
import React, {FC, useContext, useEffect, useRef} from "react";
import {makeStyles} from "tss-react/mui";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {LocaleContext} from "@/app2/shared/contexts";
import {useTodayLearning} from "@/app2/pages/HomePage/hooks/useTodayLearning";
import {TodayLearningCard} from "@/app2/pages/HomePage/TodayLearningSection/TodayLearningCard";
import EventAvailable from "@mui/icons-material/EventAvailable";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {palette} from "@/constants";
import {TodayLearningCardSkeleton} from "@/app2/pages/HomePage/TodayLearningSection/TodayLearningCardSkeleton";
import SectionHeader from "../Components/SectionHeader";

const useStyles = makeStyles()((theme) => ({
	container: {
		width: "100%",
		marginBottom: theme.customSpacing.carouselGap
	},
	scrollContainer: {
		display: "flex",
		flexWrap: "nowrap",
		overflowX: "auto",
		gap: "10px",
		paddingBottom: theme.spacing(2),
		scrollbarWidth: "none",
		"&::-webkit-scrollbar": {
			display: "none"
		},
		cursor: "grab",
		"&:active": {
			cursor: "grabbing"
		},
		userSelect: "none"
	},
	cardsContainer: {
		position: "relative",
		width: "100%"
	},
	gradientOverlayRight: {
		position: "absolute",
		top: 0,
		right: 0,
		width: "40px",
		height: "100%",
		background: "linear-gradient(to right, rgba(14, 14, 14, 0) 0%, rgba(14, 14, 14, 0.3) 50%, rgba(14, 14, 14, 0.7) 80%, #0E0E0E 100%)",
		pointerEvents: "none",
		zIndex: 1
	},
	gradientOverlayLeft: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "40px",
		height: "100%",
		background: "linear-gradient(to left, rgba(14, 14, 14, 0) 0%, rgba(14, 14, 14, 0.3) 50%, rgba(14, 14, 14, 0.7) 80%, #0E0E0E 100%)",
		pointerEvents: "none",
		zIndex: 1
	},
	nothingScheduledContainer: {
		minHeight: "476px",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		padding: "50px",
		textAlign: "center",
		boxSizing: "border-box",
		"svg": {
			margin: "10px auto",
			width: "100px",
			height: "100px",
			"path": {
				fill: palette.greySolid
			}
		}
	}
}));

export const TodayLearningSection: FC = () => {
	const {t} = useSSTranslation();
	const {classes} = useStyles();
	const [{locale}] = useContext(LocaleContext);
	const {todayLearning, isLoading, completedSomethingToday, displayTimezone} = useTodayLearning(locale);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const SCROLL_AMOUNT = 330;
	const [canScrollLeft, setCanScrollLeft] = React.useState(false);
	const [canScrollRight, setCanScrollRight] = React.useState(false);

	const scrollLeft = (): void => {
		scrollRef.current?.scrollBy({
			left: -SCROLL_AMOUNT,
			behavior: "smooth"
		});
	};

	const scrollRight = (): void => {
		scrollRef.current?.scrollBy({
			left: SCROLL_AMOUNT,
			behavior: "smooth"
		});
	};

	const updateScrollButtons = (): void => {
		const el = scrollRef.current;
		if (!el) return;

		setCanScrollLeft(el.scrollLeft > 0);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
	};

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;

		updateScrollButtons();

		el.addEventListener("scroll", updateScrollButtons);
		window.addEventListener("resize", updateScrollButtons);

		return () => {
			el.removeEventListener("scroll", updateScrollButtons);
			window.removeEventListener("resize", updateScrollButtons);
		};
	}, [todayLearning]);

	return (
		<Box className={classes.container}>
			<SectionHeader
				title={t("home_today_learning", "Today's Learning")}
				badgeContent={todayLearning?.length}
				onScrollLeft={scrollLeft}
				onScrollRight={scrollRight}
				canScrollLeft={canScrollLeft}
				canScrollRight={canScrollRight}
			/>
			<Box className={classes.cardsContainer}>
				{!isLoading && canScrollLeft && <Box className={classes.gradientOverlayLeft} />}
				{isLoading ? <TodayLearningCardSkeleton/> :
					<Box
						ref={scrollRef}
						className={classes.scrollContainer}
					>
						{todayLearning?.map((item) => (
							<TodayLearningCard key={item.id} module={item as any} timeZone={displayTimezone} />
						))}
					</Box>}
				{!isLoading && canScrollRight && <Box className={classes.gradientOverlayRight} />}
			</Box>
			{!isLoading && todayLearning?.length === 0 && (
				<Box
					className={classes.nothingScheduledContainer}
					data-test-id={completedSomethingToday ? "training-feed-all-completed" : "training-feed-nothing-scheduled"}
					role="status"
					aria-label={completedSomethingToday
						? "All training modules completed for today"
						: "No training modules scheduled for this day"}
				>
					{completedSomethingToday ? (
						<>
							<CheckCircleOutlineIcon aria-hidden="true" color="primary" sx={{ width: 100, height: 100 }} />
							<Typography variant="h5" data-test-id="training-feed-all-completed-text">
								{t("training_feed_all_completed_today" as Parameters<typeof t>[0], "Nice work... you're done for today!")}
							</Typography>
						</>
					) : (
						<>
							<EventAvailable aria-hidden="true" />
							<Typography variant="h5" data-test-id="training-feed-nothing-scheduled-text">
								{t("training_feed_nothing_scheduled", "No training scheduled for today.")}
							</Typography>
						</>
					)}
				</Box>
			)}
		</Box>
	);
};

export default TodayLearningSection;
